import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getAccessToken, setAccessToken, callOnRefreshFailed } from '../auth/tokenStore';

const baseURL = process.env.REACT_APP_API_BASE_URL || '';

export const plainApi = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

const api = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;
let subscribers: Array<(token: string | null) => void> = [];

const subscribeTokenRefresh = (cb: (token: string | null) => void) => {
  subscribers.push(cb);
};

const onRefreshed = (token: string | null) => {
  subscribers.forEach((cb) => cb(token));
  subscribers = [];
};

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers ?? {};
    if (!config.headers['Authorization']) config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = (async () => {
          try {
            const resp = await plainApi.post('/api/auth/refresh');
            const newToken = resp.data?.accessToken ?? null;
            setAccessToken(newToken);
            return newToken;
          } catch (e) {
            setAccessToken(null);
            callOnRefreshFailed();
            return null;
          } finally {
            isRefreshing = false;
          }
        })();
      }

      return new Promise((resolve, reject) => {
        subscribeTokenRefresh(async (token) => {
          if (token) {
            originalRequest._retry = true;
            originalRequest.headers = originalRequest.headers ?? {};
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            try {
              const resp = await api.request(originalRequest);
              resolve(resp);
            } catch (err) {
              reject(err);
            }
          } else {
            reject(error);
          }
        });

        refreshPromise!.then((t) => onRefreshed(t)).catch(() => onRefreshed(null));
      });
    }
    return Promise.reject(error);
  }
);

export default api;

